import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SubscriptionSummary } from '../../types/subscription';
import {
  selectDemoEndTimestamp,
  selectDemoExcludedBaseAssets,
  selectDemoStartTimestamp,
  selectIsDemo,
} from '../slices/demo';
import { selectSubscriptionSummaries } from '../slices/subscription';
import {
  DemoPortfolioSeriesItem,
  useGetDemoPortfolioSeriesQuery,
} from '../services/demoApi';

/** Look up the resampled value at-or-before a timestamp in a sorted series. */
const valueAtOrBefore = (
  series: { timestamp: number; value: number }[],
  ts: number,
): number => {
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i].timestamp <= ts) return series[i].value;
  }
  return 0;
};

const lastInWindow = (
  series: { timestamp: number; value: number }[],
  endTs: number | null,
): number => {
  if (!series.length) return 0;
  if (endTs == null) return series[series.length - 1].value;
  for (let i = series.length - 1; i >= 0; i--) {
    if (series[i].timestamp <= endTs) return series[i].value;
  }
  return 0;
};

/**
 * Rebase a subscription summary against a (startTimestamp, endTimestamp)
 * window so its totals and profits reflect "joined at startTimestamp, exited
 * at endTimestamp" instead of the full SPC lifetime.
 *
 *  - `scale = startBalanceInUsdt / valueAtStart` ⇒ asset enters the window at
 *    exactly its native start balance.
 *  - Total = `valueAtEnd × scale`. NAP uses its own scale (so both trading
 *    and buy-and-hold lines coincide at the window's left edge).
 *  - When the asset's own history starts after `startTimestamp`, no rebase
 *    applies — it joins the window at its native start.
 *  - `current.{stable, asset}` is SPC's open-position P&L and has no window-
 *    time interpretation, so it's preserved as-is.
 */
const rebaseSummary = (
  sub: SubscriptionSummary,
  series: DemoPortfolioSeriesItem | undefined,
  startTs: number | null,
  endTs: number | null,
): SubscriptionSummary => {
  if (!series || (startTs == null && endTs == null)) return sub;
  const sorted = [...series.dataPoints].sort(
    (a, b) => a.timestamp - b.timestamp,
  );
  if (!sorted.length) return sub;

  const assetFirstTs = sorted[0].timestamp;
  const effectiveStart =
    startTs != null && startTs > assetFirstTs ? startTs : null;
  const valueAtStart = effectiveStart
    ? valueAtOrBefore(sorted, effectiveStart)
    : series.startBalanceInUsdt;
  const scale =
    effectiveStart && valueAtStart > 0
      ? series.startBalanceInUsdt / valueAtStart
      : 1;

  const valueAtEnd = lastInWindow(sorted, endTs);
  if (valueAtEnd <= 0) return sub;
  const newTotal = valueAtEnd * scale;

  const baseStable = series.startBalanceInUsdt || 1;
  const newTotalStableAbs = newTotal - baseStable;
  const newTotalStableRel = baseStable ? newTotalStableAbs / baseStable : 0;

  // NAP rebase: independent scale so both lines land at startBalance at the
  // window's left edge (reusing the trading scale made them diverge).
  const sortedNap = [...series.napDataPoints].sort(
    (a, b) => a.timestamp - b.timestamp,
  );
  let napScale = 1;
  if (effectiveStart) {
    const napAtStart = valueAtOrBefore(sortedNap, effectiveStart);
    if (napAtStart > 0) napScale = series.startBalanceInUsdt / napAtStart;
  }
  const napAtEnd = lastInWindow(sortedNap, endTs) * napScale;
  const newNoAlgoAbs = newTotal - napAtEnd;
  const newNoAlgoRel = napAtEnd > 0 ? newNoAlgoAbs / napAtEnd : 0;

  const currentPrice = sub.latestPrice || 0;
  const newAssetBalance = sub.assetBalance * scale;
  const newStableBalance = sub.stableBalance * scale;

  // Fees on the latestTransaction are lifetime cumulative; without per-tx
  // data we can't extract just the window's fees, but they're proportional
  // to capital so scaling by the same factor approximates the rebased view.
  const rebasedLatestTransaction = sub.latestTransaction
    ? {
        ...sub.latestTransaction,
        totalFee: ((sub.latestTransaction as any).totalFee ?? 0) * scale,
        currentFee: ((sub.latestTransaction as any).currentFee ?? 0) * scale,
      }
    : sub.latestTransaction;

  return {
    ...sub,
    totalBalanceInStable: newTotal,
    totalBalanceInAsset: currentPrice ? newTotal / currentPrice : 0,
    assetBalance: newAssetBalance,
    stableBalance: newStableBalance,
    assetBalanceInStable: newAssetBalance * currentPrice,
    stableBalanceInAsset: currentPrice ? newStableBalance / currentPrice : 0,
    latestTransaction: rebasedLatestTransaction,
    profits: sub.profits
      ? {
          ...sub.profits,
          total: {
            stable: {
              absolute: newTotalStableAbs,
              relative: newTotalStableRel,
            },
            asset: sub.profits.total.asset,
          },
          noAlgorithmicProgressionProfit: {
            absolute: newNoAlgoAbs,
            relative: newNoAlgoRel,
          },
          costBasis: {
            stable: baseStable,
            asset: sub.profits.costBasis.asset,
          },
        }
      : sub.profits,
  };
};

interface RebasedResult {
  /** Subscriptions with the demo exclusion list applied AND each surviving sub
   *  rebased against the date window. Outside of demo, this is the raw list. */
  visible: SubscriptionSummary[];
  /** Convenience map keyed by baseAsset for lookups by Subscription detail. */
  byBaseAsset: Record<string, SubscriptionSummary>;
}

/**
 * Single source of truth for demo-mode subscription transforms.
 *
 * Returns the exclusion-filtered + date-rebased subscriptions so the Overview
 * cards, the Settings dropdown's percentage list, and the Subscription detail
 * page all read the same windowed values without redoing the math.
 */
export const useDemoRebasedSubscriptions = (): RebasedResult => {
  const rawData = useSelector(selectSubscriptionSummaries);
  const isDemo = useSelector(selectIsDemo);
  const excluded = useSelector(selectDemoExcludedBaseAssets);
  const startTs = useSelector(selectDemoStartTimestamp);
  const endTs = useSelector(selectDemoEndTimestamp);
  const { data: demoSeriesResp } = useGetDemoPortfolioSeriesQuery(undefined, {
    skip: !isDemo,
  });

  return useMemo(() => {
    if (!isDemo) {
      const map: Record<string, SubscriptionSummary> = {};
      for (const s of rawData) map[s.baseAsset] = s;
      return { visible: rawData, byBaseAsset: map };
    }
    const blocked = new Set(excluded);
    const filtered = excluded.length
      ? rawData.filter((s) => !blocked.has(s.baseAsset))
      : rawData;
    const noWindow = startTs == null && endTs == null;
    const byAsset = new Map<string, DemoPortfolioSeriesItem>();
    for (const s of demoSeriesResp?.series ?? []) byAsset.set(s.baseAsset, s);
    const visible =
      noWindow || !demoSeriesResp?.series
        ? filtered
        : filtered.map((sub) =>
            rebaseSummary(sub, byAsset.get(sub.baseAsset), startTs, endTs),
          );
    const map: Record<string, SubscriptionSummary> = {};
    for (const s of visible) map[s.baseAsset] = s;
    return { visible, byBaseAsset: map };
  }, [rawData, isDemo, excluded, startTs, endTs, demoSeriesResp]);
};

export { rebaseSummary };
